import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle, Users, TrendingUp, DollarSign, 
  Clock, Shield, Repeat, Award, ChevronDown, ChevronUp,
  Zap, Target, BarChart3, Link2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: Clock, label: "60-day referral window" },
  { icon: Repeat, label: "Lifetime commissions after lock" },
  { icon: DollarSign, label: "Direct deposit payouts" },
  { icon: Users, label: "Open to all members" },
];

const steps = [
  { num: "01", title: "Join AREA", desc: "Sign up for free. Every member is automatically enrolled in the partner program." },
  { num: "02", title: "Share your link", desc: "Use your unique referral link to share AREA with agents, teams, and creators." },
  { num: "03", title: "Earn commissions", desc: "When referrals upgrade to a paid plan, your attribution locks for life." },
  { num: "04", title: "Get paid", desc: "Track recurring earnings and receive Stripe payouts when you hit the threshold." },
];

const audiences = [
  { icon: Users, title: "Agents & Teams", desc: "Refer individual agents or entire brokerage teams into AREA membership." },
  { icon: Zap, title: "Creators & Influencers", desc: "Monetize your audience with recurring commissions from a premium real estate platform." },
  { icon: Target, title: "Educators & Coaches", desc: "Recommend AREA to students and coaching clients as a career growth resource." },
  { icon: BarChart3, title: "Strategic Partners", desc: "Build long-term referral revenue through industry partnerships and joint ventures." },
];

const faqs = [
  { q: "What does lifetime commissions mean?", a: "Once a referral upgrades to a paid membership and your attribution locks, you earn commissions on every future qualifying payment they make — renewals, upgrades, and more — for the lifetime of their membership." },
  { q: "When does attribution lock?", a: "Attribution locks after the referred member's first qualifying paid conversion. Before that, last-click attribution applies within the 60-day referral window." },
  { q: "How do renewal commissions work?", a: "When a referred member renews their paid membership, a new commission event is generated at your locked commission rate. This happens automatically each billing cycle." },
  { q: "What happens if a referred member gets a refund?", a: "If clawbacks are enabled, the corresponding commission is reversed. This protects program integrity and ensures commissions are based on actual retained revenue." },
  { q: "What is the payout threshold?", a: "The default payout threshold is $200. Once your approved balance reaches this amount and your bank account information is on file, payouts are processed via direct deposit." },
  { q: "Is the program open to free members?", a: "Yes. All AREA members — Free, Pro, and Founder — are automatically enrolled and can start referring immediately." },
];

export default function AffiliateLanding() {
  const [settings, setSettings] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetch(`${API}/affiliate/settings/public`).then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-landing">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/area-logo.png" alt="AREA" className="h-10" />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <Link to="/affiliate" className="text-[#1e3a5f] font-semibold">Affiliate</Link>
            <Link to="/affiliate/influencers" className="hover:text-[#1e3a5f] transition-colors">For Creators</Link>
            <Link to="/affiliate/brokerages" className="hover:text-[#1e3a5f] transition-colors">For Brokerages</Link>
            <Link to="/login" className="hover:text-[#1e3a5f] transition-colors">Sign In</Link>
          </div>
          <Link to="/register">
            <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6">Join AREA</Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f0f4ff] to-white"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1e3a5f]/5 text-[#1e3a5f] text-sm font-semibold mb-8">
            <Award className="w-4 h-4" />
            Partner Program
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0f1f33] leading-tight tracking-tight mb-6" data-testid="affiliate-hero-title">
            Grow with AREA
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed mb-10">
            Share AREA with agents, teams, creators, and industry professionals — and earn 
            lifetime commissions when your referrals join and continue paying.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/register">
              <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold shadow-lg">
                Start Referring <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 px-8 py-6 text-lg">
                View How It Works
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <b.icon className="w-4 h-4 text-[#2563eb]" />
                <span>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lifetime Commissions */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">Recurring Revenue</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33] mb-4">Earn beyond the first signup</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Unlike one-time referral bonuses, AREA's partner program pays you on every qualifying transaction your referred members make.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Attribution locks for life", desc: "After a referred member's first paid conversion, your referral is locked permanently. No one can override it (unless admin intervenes)." },
              { title: "Renewals keep paying", desc: "Every time your referred member renews their Pro or Founder membership, a new commission is generated at your locked rate." },
              { title: "Upgrades increase your upside", desc: "When a referred member upgrades from Pro to Founder, you earn a commission on the upgrade difference at the applicable rate." },
              { title: "Full visibility in your dashboard", desc: "Track every click, signup, conversion, renewal, and payout in real time from your partner dashboard." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-[#0f1f33]">{item.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed pl-8">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Rates */}
      {settings && (
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">Commission Rates</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33] mb-4">Clear economics, no hidden rules</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {[
                { tier: "Free", rate: settings.free_tier_commission_rate, price: "$0", color: "border-gray-200" },
                { tier: "Pro", rate: settings.pro_tier_commission_rate, price: "$99/yr", color: "border-[#2563eb]" },
                { tier: "Founder", rate: settings.founder_tier_commission_rate, price: "$999", color: "border-amber-400" },
              ].map((t, i) => (
                <div key={i} className={`bg-white rounded-2xl p-8 border-2 ${t.color} text-center shadow-sm`}>
                  <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">{t.tier} Tier</p>
                  <p className="text-4xl font-bold text-[#0f1f33] mb-1">{t.rate}%</p>
                  <p className="text-sm text-gray-400">on {t.price} membership</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who It's For */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">Who It's For</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Built for partners of all kinds</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-[#2563eb]/10 flex items-center justify-center mb-4">
                  <a.icon className="w-6 h-6 text-[#2563eb]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1f33] mb-2">{a.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20" id="how-it-works">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Four steps to recurring revenue</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-5xl font-bold text-[#2563eb]/10 mb-3">{s.num}</div>
                <h3 className="text-lg font-semibold text-[#0f1f33] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#fafbfd]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#2563eb] font-semibold text-sm tracking-wider uppercase mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1f33]">Common questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-semibold text-[#0f1f33] pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-50 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0f1f33]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to start earning?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-xl mx-auto">
            Join AREA for free and start sharing your referral link today. Your first referred member could lock in lifetime commissions.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-10 py-6 text-lg font-semibold shadow-lg">
              Join AREA for Free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <img src="/images/area-logo.png" alt="AREA" className="h-6" />
          </div>
          <div className="flex gap-6">
            <Link to="/affiliate/terms" className="hover:text-gray-600">Affiliate Terms</Link>
            <Link to="/privacy" className="hover:text-gray-600">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-600">Terms</Link>
            <Link to="/contact" className="hover:text-gray-600">Support</Link>
          </div>
          <span>&copy; {new Date().getFullYear()} American Real Estate Alliance</span>
        </div>
      </footer>
    </div>
  );
}
